/**
 * locationLabels.js — SINGLE SOURCE OF TRUTH for localized country / city
 * display labels used by active-filter chips and any other UI that needs to
 * render a human-readable location name from a stable slug key.
 *
 * Why this file exists
 * --------------------
 * The backend `/api/student-hub/countries` endpoint uses slug keys
 * (`egypt`, `saudi_arabia`, `port_sudan`, …) as stable, URL-safe values for
 * filter state. Raw slugs must NEVER leak into the UI — especially in Arabic
 * mode, where users reported seeing `egypt` instead of `مصر`.
 *
 * The original Home/Shop implementations read `locationCountries` from
 * `useLocationFilter`, which is **async**. During the first render (and on
 * any route where the hook hasn't finished fetching), the lookup returned an
 * empty array and the chip fell back to `return key`, leaking the slug.
 *
 * This file ships a synchronous mirror of the backend catalogue. The chip
 * resolver consults:
 *   1. Any dynamically fetched data (so newly-added backend entries work).
 *   2. This static catalogue (covers every entry the backend currently ships).
 *   3. `humanizeLocationKey()` as a last-resort cosmetic fallback so even an
 *      unknown slug never appears unformatted (e.g. `new_key` → `New Key`).
 *
 * Home, Shop, and any new surface with an active-filter chip MUST call
 * `resolveCountryLabel` / `resolveCityLabel` from this module — do NOT
 * reimplement the logic per page.
 */

// Mirror of /api/student-hub/countries name_en/name_ar fields.
// Regenerated any time the backend catalogue changes — see the helper
// script comment at the bottom of this file.
export const STATIC_COUNTRY_LABELS = {
  'sudan': { en: 'Sudan', ar: 'السودان' },
  'egypt': { en: 'Egypt', ar: 'مصر' },
  'libya': { en: 'Libya', ar: 'ليبيا' },
  'tunisia': { en: 'Tunisia', ar: 'تونس' },
  'algeria': { en: 'Algeria', ar: 'الجزائر' },
  'morocco': { en: 'Morocco', ar: 'المغرب' },
  'saudi_arabia': { en: 'Saudi Arabia', ar: 'المملكة العربية السعودية' },
  'uae': { en: 'United Arab Emirates', ar: 'الإمارات العربية المتحدة' },
  'qatar': { en: 'Qatar', ar: 'قطر' },
  'kuwait': { en: 'Kuwait', ar: 'الكويت' },
  'oman': { en: 'Oman', ar: 'عمان' },
  'bahrain': { en: 'Bahrain', ar: 'البحرين' },
  'jordan': { en: 'Jordan', ar: 'الأردن' },
  'iraq': { en: 'Iraq', ar: 'العراق' },
  'ethiopia': { en: 'Ethiopia', ar: 'إثيوبيا' },
  'kenya': { en: 'Kenya', ar: 'كينيا' },
  'uganda': { en: 'Uganda', ar: 'أوغندا' },
  'tanzania': { en: 'Tanzania', ar: 'تنزانيا' },
  'somalia': { en: 'Somalia', ar: 'الصومال' },
  'chad': { en: 'Chad', ar: 'تشاد' },
  'eritrea': { en: 'Eritrea', ar: 'إريتريا' },
  'south_sudan': { en: 'South Sudan', ar: 'جنوب السودان' },
  // West / Central / Southern Africa (added 2026-04-28)
  'ghana': { en: 'Ghana', ar: 'غانا' },
  'guinea': { en: 'Guinea', ar: 'غينيا' },
  'mauritania': { en: 'Mauritania', ar: 'موريتانيا' },
  'mozambique': { en: 'Mozambique', ar: 'موزمبيق' },
  'mali': { en: 'Mali', ar: 'مالي' },
  'congo': { en: 'Congo', ar: 'الكونغو' },
  'zambia': { en: 'Zambia', ar: 'زامبيا' },
};

export const STATIC_CITY_LABELS = {
  'khartoum': { en: 'Khartoum', ar: 'الخرطوم' },
  'omdurman': { en: 'Omdurman', ar: 'أم درمان' },
  'khartoum_north': { en: 'Khartoum North', ar: 'الخرطوم بحري' },
  'port_sudan': { en: 'Port Sudan', ar: 'بورتسودان' },
  'wad_madani': { en: 'Wad Madani', ar: 'ود مدني' },
  'kassala': { en: 'Kassala', ar: 'كسلا' },
  'gedarif': { en: 'Gedarif', ar: 'القضارف' },
  'nyala': { en: 'Nyala', ar: 'نيالا' },
  'al_fashir': { en: 'El Fasher', ar: 'الفاشر' },
  'el_obeid': { en: 'El Obeid', ar: 'الأبيض' },
  'atbara': { en: 'Atbara', ar: 'عطبرة' },
  'dongola': { en: 'Dongola', ar: 'دنقلا' },
  'shendi': { en: 'Shendi', ar: 'شندي' },
  'cairo': { en: 'Cairo', ar: 'القاهرة' },
  'alexandria': { en: 'Alexandria', ar: 'الإسكندرية' },
  'giza': { en: 'Giza', ar: 'الجيزة' },
  'aswan': { en: 'Aswan', ar: 'أسوان' },
  'luxor': { en: 'Luxor', ar: 'الأقصر' },
  'mansoura': { en: 'Mansoura', ar: 'المنصورة' },
  'tripoli': { en: 'Tripoli', ar: 'طرابلس' },
  'benghazi': { en: 'Benghazi', ar: 'بنغازي' },
  'misrata': { en: 'Misrata', ar: 'مصراتة' },
  'tunis': { en: 'Tunis', ar: 'تونس العاصمة' },
  'sfax': { en: 'Sfax', ar: 'صفاقس' },
  'sousse': { en: 'Sousse', ar: 'سوسة' },
  'algiers': { en: 'Algiers', ar: 'الجزائر العاصمة' },
  'oran': { en: 'Oran', ar: 'وهران' },
  'constantine': { en: 'Constantine', ar: 'قسنطينة' },
  'casablanca': { en: 'Casablanca', ar: 'الدار البيضاء' },
  'rabat': { en: 'Rabat', ar: 'الرباط' },
  'marrakech': { en: 'Marrakech', ar: 'مراكش' },
  'fes': { en: 'Fes', ar: 'فاس' },
  'riyadh': { en: 'Riyadh', ar: 'الرياض' },
  'jeddah': { en: 'Jeddah', ar: 'جدة' },
  'makkah': { en: 'Makkah', ar: 'مكة المكرمة' },
  'madinah': { en: 'Madinah', ar: 'المدينة المنورة' },
  'dammam': { en: 'Dammam', ar: 'الدمام' },
  'dubai': { en: 'Dubai', ar: 'دبي' },
  'abu_dhabi': { en: 'Abu Dhabi', ar: 'أبوظبي' },
  'sharjah': { en: 'Sharjah', ar: 'الشارقة' },
  'doha': { en: 'Doha', ar: 'الدوحة' },
  'kuwait_city': { en: 'Kuwait City', ar: 'مدينة الكويت' },
  'muscat': { en: 'Muscat', ar: 'مسقط' },
  'manama': { en: 'Manama', ar: 'المنامة' },
  'amman': { en: 'Amman', ar: 'عمّان' },
  'irbid': { en: 'Irbid', ar: 'إربد' },
  'zarqa': { en: 'Zarqa', ar: 'الزرقاء' },
  'baghdad': { en: 'Baghdad', ar: 'بغداد' },
  'basra': { en: 'Basra', ar: 'البصرة' },
  'erbil': { en: 'Erbil', ar: 'أربيل' },
  'addis_ababa': { en: 'Addis Ababa', ar: 'أديس أبابا' },
  'nairobi': { en: 'Nairobi', ar: 'نيروبي' },
  'mombasa': { en: 'Mombasa', ar: 'مومباسا' },
  'kampala': { en: 'Kampala', ar: 'كمبالا' },
  'dar_es_salaam': { en: 'Dar es Salaam', ar: 'دار السلام' },
  'mogadishu': { en: 'Mogadishu', ar: 'مقديشو' },
  'ndjamena': { en: "N'Djamena", ar: 'نجامينا' },
  'asmara': { en: 'Asmara', ar: 'أسمرة' },
  'juba': { en: 'Juba', ar: 'جوبا' },
  // West / Central / Southern Africa cities (added 2026-04-28)
  'accra': { en: 'Accra', ar: 'أكرا' },
  'kumasi': { en: 'Kumasi', ar: 'كوماسي' },
  'conakry': { en: 'Conakry', ar: 'كوناكري' },
  'nouakchott': { en: 'Nouakchott', ar: 'نواكشوط' },
  'nouadhibou': { en: 'Nouadhibou', ar: 'نواذيبو' },
  'maputo': { en: 'Maputo', ar: 'مابوتو' },
  'beira': { en: 'Beira', ar: 'بييرا' },
  'bamako': { en: 'Bamako', ar: 'باماكو' },
  'timbuktu': { en: 'Timbuktu', ar: 'تمبكتو' },
  'kinshasa': { en: 'Kinshasa', ar: 'كينشاسا' },
  'brazzaville': { en: 'Brazzaville', ar: 'برازافيل' },
  'lubumbashi': { en: 'Lubumbashi', ar: 'لوبومباشي' },
  'lusaka': { en: 'Lusaka', ar: 'لوساكا' },
  'ndola': { en: 'Ndola', ar: 'ندولا' },
};

/**
 * Humanize an unknown slug key so we never leak raw `snake_case` into the UI.
 * Pure cosmetic — used only when both dynamic and static catalogues miss.
 */
export function humanizeLocationKey(key) {
  if (!key || typeof key !== 'string') return '';
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve a country slug to its localized display name.
 *
 * @param {string} key           slug like 'egypt' or 'saudi_arabia'
 * @param {boolean} isArabic     current UI language
 * @param {Array}  [dynamicList] optional array from /api/student-hub/countries
 * @returns {string} localized name — never a raw slug
 */
export function resolveCountryLabel(key, isArabic, dynamicList) {
  if (!key) return '';
  if (Array.isArray(dynamicList) && dynamicList.length) {
    const match = dynamicList.find((c) => c.key === key);
    if (match) return isArabic ? (match.name_ar || match.name_en) : (match.name_en || match.name_ar);
  }
  const entry = STATIC_COUNTRY_LABELS[key];
  if (entry) return isArabic ? entry.ar : entry.en;
  return humanizeLocationKey(key);
}

/**
 * Resolve a city slug to its localized display name.
 *
 * @param {string} cityKey       slug like 'cairo' or 'port_sudan'
 * @param {boolean} isArabic     current UI language
 * @param {Array}   [cityList]   optional list of cities for the current country
 * @returns {string} localized name — never a raw slug
 */
export function resolveCityLabel(cityKey, isArabic, cityList) {
  if (!cityKey) return '';
  if (Array.isArray(cityList) && cityList.length) {
    const match = cityList.find((c) => c.key === cityKey);
    if (match) return isArabic ? (match.name_ar || match.name_en) : (match.name_en || match.name_ar);
  }
  const entry = STATIC_CITY_LABELS[cityKey];
  if (entry) return isArabic ? entry.ar : entry.en;
  return humanizeLocationKey(cityKey);
}

/*
  To regenerate the static tables above after a backend catalogue change:
    curl ${REACT_APP_BACKEND_URL}/api/student-hub/countries | python3 -c "
    import sys,json
    d=json.load(sys.stdin)
    # … print STATIC_COUNTRY_LABELS / STATIC_CITY_LABELS entries …
    "
*/
